import { useMemo } from 'react'

import { FilterDefinition, FiltersDefinition } from '@factorialco/f0-react/dist/experimental'

import i18n from 'modules/core/lib/i18n'

import { SPACES } from 'modules/documents/lib/constants'
import type { SPACE } from 'modules/documents/lib/constants'
import { useGetEmployeesFilterData } from 'modules/documents/hooks/useGetEmployeesFilterData'
import { useGetFolders } from 'modules/documents/hooks/useGetFolders'

export type TrashBinFilterKeys = 'deletedAt' | 'employee' | 'folder'

export type TrashBinFiltersDefinition = FiltersDefinition

export const useFilters = (
  space: SPACE
): {
  filters: TrashBinFiltersDefinition
  loading: boolean
} => {
  const isEmployeeSpace = space === SPACES.EMPLOYEE_MY_DOCUMENTS

  const { employees, loading: loadingEmployees } = useGetEmployeesFilterData({
    skip: !isEmployeeSpace,
  })

  const { folders, loading: loadingFolders } = useGetFolders({ space })

  const filters: TrashBinFiltersDefinition = useMemo(() => {
    const result: TrashBinFiltersDefinition = {
      deletedAt: {
        label: i18n.t('documents.employee.list.deleted_at'),
        type: 'date',
      } satisfies FilterDefinition,
    }

    if (isEmployeeSpace) {
      result.employee = {
        label: i18n.t('documents.employee.list.employee'),
        type: 'in',
        options: {
          options: employees.map((emp) => ({
            label: emp.fullName,
            value: emp.id,
          })),
        },
      } satisfies FilterDefinition
    }

    result.folder = {
      label: i18n.t('documents.employee.list.folder'),
      type: 'in',
      options: {
        options: folders.map((f) => ({
          label: f.name,
          value: f.id,
        })),
      },
    } satisfies FilterDefinition

    return result
  }, [isEmployeeSpace, employees, folders])

  return { filters, loading: loadingEmployees || loadingFolders }
}
